import {
  IRecurringBooking,
  IRecurringBookingDetail,
  IRecurringBookingRequest,
  IRecurringBookingSaveRequest,
  IRecurringBookingSubset,
  ITablePickerData,
  IWeeklyScheduleSubset,
  ISectionDetails,
  ITableSeaterRecurringProps,
} from './type';
import { IBooking, ITableItem, ICustomer } from 'shared-types/SharedTypes';
import DateUtilsService from 'external-services/date-utils-service';
import moment, { Moment } from 'moment';
import { isEmpty } from 'lodash-es';
import { AxiosInstance } from 'axios';
import NetworkRequestsService from 'external-services/network-requests-service/index';
import { IAuthService } from 'external-services/diary-admin-service/types';

const NS = 'RecurringBookingService';

const api: AxiosInstance = NetworkRequestsService.getGlobalAPI();

class RecurringBookingService {
  private APIBASE: string;
  private getVenueId: () => number;

  setup(apiBase: string, getVenueId: () => number): void {
    this.APIBASE = apiBase;
    this.getVenueId = getVenueId;
  }

  // // Populate recurring booking request
  MakeRecurringBookingRequestForSaveBooking(
    recurringBooking: IRecurringBookingSaveRequest,
    bookingInfo: IBooking
  ) {
    const bookingRequest: IRecurringBookingRequest = {
      recurringBooking: {
        customer: recurringBooking.customer,
        bookingRepeat: recurringBooking.bookingRepeat,
        repeatTimes: recurringBooking.repeatTimes,
        repeatFrequency: recurringBooking.repeatFrequency,
        startTime: DateUtilsService.DateToDateTimeString(bookingInfo.time),
        endTime: recurringBooking.endTime,
      },
    };

    return bookingRequest;
  }

  async GenerateRecurringBookings(
    request: IRecurringBookingRequest
  ): Promise<IRecurringBookingDetail[]> {
    let response: IRecurringBookingDetail[] = [];
    const url = `${
      this.APIBASE
    }/restaurants/venues/${this.getVenueId()}/generate-recurring-bookings`;

    try {
      const apiResponse = await api.post(url, request);
      if (apiResponse && apiResponse.data) {
        response = apiResponse.data;
      }
    } catch (e) {
      console.warn(NS, 'GenerateRecurringBookings', e);
    }

    return response;
  }

  async getRecurringBookings(recurringBookingId: string): Promise<IRecurringBookingDetail> {
    let response: IRecurringBookingDetail;
    const url = `${
      this.APIBASE
    }/restaurants/venues/${this.getVenueId()}/recurring-bookings/${recurringBookingId}`;

    try {
      const apiResponse = await api.get(url);
      if (apiResponse && apiResponse.data) {
        response = apiResponse.data;
      }
    } catch (e) {
      console.warn(NS, 'GenerateRecurringBookings', e);
    }

    return response;
  }

  async getWeeklySchedules(): Promise<IWeeklyScheduleSubset[]> {
    let response: IWeeklyScheduleSubset[];
    const url = `${this.APIBASE}/restaurants/venues/${this.getVenueId()}/weekly-schedules`;

    try {
      const apiResponse = await api.get(url);
      if (apiResponse && apiResponse.data) {
        response = apiResponse.data;
      }
    } catch (e) {
      console.warn(NS, 'GenerateRecurringBookings', e);
    }

    return response;
  }

  async ValidateRecurringBooking(
    request: IRecurringBookingSubset[]
  ): Promise<IRecurringBookingDetail[]> {
    let response: IRecurringBookingDetail[] = [];
    const url = `${this.APIBASE}/restaurants/venues/${this.getVenueId()}/validate-bookings`;

    try {
      const apiResponse = await api.post(url, request);
      if (apiResponse && apiResponse.data) {
        response = apiResponse.data;
      }
    } catch (e) {
      console.warn(NS, 'GenerateRecurringBookings', e);
    }

    return response;
  }

  async SaveRecurringBooking(request: IRecurringBookingRequest): Promise<boolean> {
    let isSavedSuccessfully = false;
    const url = `${this.APIBASE}/restaurants/venues/${this.getVenueId()}/recurring-bookings`;

    try {
      const apiResponse = await api.post(url, request);
      if (apiResponse && apiResponse.status === 200) {
        isSavedSuccessfully = true;
      }
    } catch (e) {
      console.warn(NS, 'GenerateRecurringBookings', e);
    }

    return isSavedSuccessfully;
  }

  async cancelRecurringBookings(bookingIds: string[]): Promise<IRecurringBookingSubset[]> {
    const url = `${this.APIBASE}/restaurants/venues/${this.getVenueId()}/CancelBookings`;

    let response: IRecurringBookingSubset[] = [];

    try {
      const apiResponse = await api.post(url, bookingIds);
      if (apiResponse && apiResponse.data) {
        response = apiResponse.data;
      }
    } catch (e) {
      console.warn(NS, 'GenerateRecurringBookings', e);
    }
    return response;
  }

  async getTablePicker(
    serviceId: string,
    bookingTime: string,
    numOfPeople: number,
    ignoreMaxOccupancy = true
  ): Promise<ITablePickerData> {
    let response: ITablePickerData = {} as ITablePickerData;

    const url = `${
      this.APIBASE
    }/restaurants/venues/${this.getVenueId()}/table-picker?venueId=${this.getVenueId()}&serviceId=${serviceId}&bookingTime=${bookingTime}&numOfPeople=${numOfPeople}&ignoreMaxOccupancy=${ignoreMaxOccupancy}`;

    try {
      const apiResponse = await api.get(url);
      if (apiResponse && apiResponse.data) {
        response = apiResponse.data;
      }
    } catch (e) {
      console.warn(NS, 'GenerateRecurringBookings', e);
    }

    return response;
  }

  checkForExpiredTime(bookingTime: Date, currentDateTime: Moment): boolean {
    const momentDate = moment(bookingTime);
    return momentDate.isBefore(currentDateTime) || momentDate.isSame(currentDateTime);
  }

  getSectionInfo(
    tableSeaterProps: ITableSeaterRecurringProps,
    tableIds: string[]
  ): ISectionDetails[] {
    let returnVal: ISectionDetails[] = [];

    const selectedTables = !isEmpty(tableSeaterProps.tableSelectionList)
      ? tableIds.map((id) =>
          tableSeaterProps.tableSelectionList.find((t: ITableItem) => t._id === id)
        )
      : [];
    if (selectedTables.every((s) => s?._id)) {
      returnVal = selectedTables
        .map((t) => t.sectionId)
        .map((id) => tableSeaterProps.sectionsLookup[id]); // return the section details from the lookup
    }
    return returnVal;
  }

  getSectionDetailsFromSectionLookup(tableIds: ITableItem[], sectionsLookup: any) {
    return tableIds.map((t) => t.sectionId).map((id) => sectionsLookup[id]); // return the section details from the lookup
  }

  // ==================named before: updateBookings==================
  public updateRecurringBookingsProps(bookingList: IRecurringBookingSubset[], booking: IBooking) {
    return bookingList.map((recurringBooking: IRecurringBookingSubset) => {
      recurringBooking.tags = booking.tags;
      recurringBooking.notes = booking.notes;
      recurringBooking.bookingLinks = booking.bookingLinks;
      return recurringBooking;
    });
  }
  public updateRecurringBookingsCustomer(
    recurringBooking: IRecurringBooking,
    tempCustomer: ICustomer,
    customerId: string
  ) {
    recurringBooking.customer = tempCustomer;
    recurringBooking.customer.id = customerId;
  }
}

const instance = new RecurringBookingService();
export default instance as RecurringBookingService;
